import { useState } from "react";
import { User, Mail, Phone, Lock, ChevronLeft, AlertTriangle, CheckCircle } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api from "../services/api";
import SuperAdminOnlyNote from "../components/SuperAdminOnlyNote";
import { isSuperAdmin } from "../utils/role";

// The backend creates every new account as a plain admin.
const ROLE_LABEL = "Admin (can manage riders and rides)";

export default function AddNewAdmin() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setShowConfirmation(true);
  };

  const confirmAddAdmin = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await api.post('/v1/admin/users', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });

      setSuccess('Admin added successfully!');
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        password: "",
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add admin. Please try again.');
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const cancelAddAdmin = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6 max-w-3xl w-full">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => window.history.back()}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
              </div>
              <p className="text-sm text-gray-500 pl-7">Add New Admin</p>
            </div>

            {!isSuperAdmin() ? (
              <SuperAdminOnlyNote />
            ) : (
            <form onSubmit={handleSubmit} className="bg-white dark:bg-black rounded-xl p-6 space-y-6 w-full">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg pl-10 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-black"
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg pl-10 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-black"
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Phone Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg pl-10 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-black"
                    placeholder="Enter phone number with country code"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg pl-10 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-black"
                    placeholder="Enter password"
                    required
                    minLength="6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Role</label>
                <p className="w-full border border-gray-200 bg-gray-50 rounded-lg px-4 py-2 text-sm text-gray-700">
                  {ROLE_LABEL}
                </p>
              </div>

              <div className="flex justify-start space-x-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`bg-orange-400 text-black font-semibold rounded-lg py-3 px-6 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-500'} transition`}
                >
                  {isLoading ? 'Adding...' : 'Add Admin'}
                </button>

              </div>

              {/* Success Message */}
              {success && (
                <div className="p-4 mb-4 text-green-700 bg-green-100 rounded-lg flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  {success}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  {error}
                </div>
              )}

              {/* Confirmation Dialog */}
              {showConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-black rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 text-orange-500">
                        <AlertTriangle className="h-6 w-6" />
                      </div>
                      <div className="ml-3 w-0 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">Confirm Admin Addition</h3>
                        <div className="mt-2 text-sm text-gray-500">
                          <p>Are you sure you want to add <span className="font-semibold">{formData.name}</span> as an admin?</p>
                          <div className="mt-2 space-y-1">
                            <p><span className="font-medium">Email:</span> {formData.email}</p>
                            <p><span className="font-medium">Phone:</span> {formData.phone}</p>
                            <p><span className="font-medium">Role:</span> {ROLE_LABEL}</p>
                          </div>
                        </div>
                        <div className="mt-4 flex space-x-3">
                          <button
                            type="button"
                            onClick={confirmAddAdmin}
                            disabled={isLoading}
                            className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-orange-500 border border-transparent rounded-md ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600'} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange-500`}
                          >
                            {isLoading ? 'Adding...' : 'Yes, Add Admin'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelAddAdmin}
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-black border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
